<script lang="ts">
	import { onMount } from 'svelte';
	import { fade } from 'svelte/transition';
	import { Splide, SplideSlide } from '@splidejs/svelte-splide';
	import '@splidejs/svelte-splide/css';
	import VimeoItem from '$lib/components/VimeoItem.svelte';

	export let videos;
	export let title = '';
	export let titleColor = '';
	export let link = '';
	export let playlist = null;

	let splideInstance;
	let isCarouselReady = false;
	let showPlaceholder = true;

	const goToNextPage = () => {
		splideInstance?.go('>');
	};

	const goToPreviousPage = () => {
		splideInstance?.go('<');
	};

	if (!link || link === '') {
		link = `/video/${videos[0].permalink_slug}?playlist=${playlist ? playlist.permalink_slug : undefined}`;
	}

	onMount(() => {
		setTimeout(() => {
			isCarouselReady = true;
			// Hide placeholder after carousel fade-in is complete
			setTimeout(() => {
				showPlaceholder = false;
			}, 400);
		}, 50);
	});
</script>

<section class="-mx-8">
	<!-- Headers and navigation are always shown -->
	<div class="m-auto flex flex-col items-center justify-start">
		<div class="flex w-full items-start justify-between gap-8 px-2 pb-4 md:px-8 md:pb-8">
			<div class="flex items-start gap-4">
				<h2 class="z-10 text-left text-xl md:text-3xl {titleColor}">{title}</h2>
			</div>
			<div class="hidden items-end justify-end gap-4 md:flex">
				<!-- Navigation buttons -->
				<button
					class="z-10 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-gray-400 bg-gray-300 transition-all ease-linear {isCarouselReady
						? 'opacity-70 hover:opacity-100'
						: 'cursor-not-allowed opacity-40'}"
					aria-label="Previous slide"
					on:click={goToPreviousPage}
					disabled={!isCarouselReady}
				>
					<svg width="48px" height="48px" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
						<g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
							<g transform="translate(-62, 0)">
								<g transform="translate(62.33, 0)">
									<path
										d="M23.745,0 C36.859,0 47.49,10.631 47.49,23.745 C47.49,36.859 36.859,47.49 23.745,47.49 C10.631,47.49 0,36.859 0,23.745 C0,10.631 10.631,0 23.745,0 Z"
										fill="#BEBEBE"
									></path>
									<polyline
										id="Path"
										stroke="#FFFFFF"
										transform="translate(23.7435, 23.745) rotate(180) translate(-23.7435, -23.745)"
										points="18.613 13.484 28.874 23.745 18.613 34.006"
									></polyline>
								</g>
							</g>
						</g>
					</svg>
				</button>
				<button
					class="z-10 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-gray-400 bg-gray-300 transition-all ease-linear {isCarouselReady
						? 'opacity-70 hover:opacity-100'
						: 'cursor-not-allowed opacity-40'}"
					aria-label="Next slide"
					on:click={goToNextPage}
					disabled={!isCarouselReady}
				>
					<svg width="48px" height="48px" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
						<g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
							<g transform="translate(-62, 0)">
								<g transform="translate(62.33, 0)">
									<path
										d="M23.745,0 C36.859,0 47.49,10.631 47.49,23.745 C47.49,36.859 36.859,47.49 23.745,47.49 C10.631,47.49 0,36.859 0,23.745 C0,10.631 10.631,0 23.745,0 Z"
										fill="#BEBEBE"
									></path>
									<polyline stroke="#FFFFFF" points="20.775 13.484 31.036 23.745 20.775 34.006"
									></polyline>
								</g>
							</g>
						</g>
					</svg>
				</button>
				<div class="m-auto ml-8 flex flex-col items-center justify-start">
					<span class="z-10 text-xl text-darkgray">
						&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
					</span>
				</div>
			</div>
		</div>

		<div class="carousel-container relative w-full px-2 md:px-8">
			<div class="carousel-wrapper">
				{#if showPlaceholder}
					<div
						class="placeholder-grid grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
					>
						{#each videos.slice(0, 4) as video}
							<div class="aspect-video w-full">
								<img
									src={video?.public_image_url
										? video.public_image_url.replace('1920x1080', '640x360')
										: '/api/placeholder/640/360'}
									alt=""
									class="h-full w-full object-cover"
									loading="eager"
								/>
							</div>
						{/each}
					</div>
				{/if}

				{#if isCarouselReady}
					<div class="carousel-content" transition:fade={{ duration: 300 }}>
						<Splide
							bind:this={splideInstance}
							options={{
								gap: '1rem',
								pagination: false,
								arrows: false,
								perPage: 4,
								type: 'slide',
								rewind: true,
								breakpoints: {
									640: { perPage: 1 },
									768: { perPage: 2 },
									1024: { perPage: 3 },
									1280: { perPage: 4 }
								}
							}}
						>
							{#each videos as video}
								<SplideSlide>
									<VimeoItem {video} playlistId={playlist ? playlist.permalink_slug : undefined} />
								</SplideSlide>
							{/each}
						</Splide>
					</div>
				{/if}
			</div>
		</div>
	</div>
</section>

<style>
	:global(.splide__track) {
		overflow: visible !important;
	}

	:global(.splide__list) {
		height: auto !important;
	}

	/* CSS Solution for dynamic height */
	.carousel-wrapper {
		position: relative;
		width: 100%;
		height: auto;
		min-height: 0;
	}

	.placeholder-grid {
		position: relative;
		width: 100%;
	}

	.carousel-content {
		position: relative;
		width: 100%;
	}

	/* Make sure Splide slides maintain proper aspect ratio */
	:global(.splide__slide) {
		height: auto !important;
	}
</style>
